/**
 * `isAuthorizationError`, vendored so the package doesn't import it from a host
 * app. This is the single copy both apps' Fay surfaces run on — the alternative
 * was a third copy alongside two that had already drifted from each other.
 *
 * SYNC: the origin is `common/utils/errors` in the `main` monolith. That path
 * isn't covered by `main`'s CODEOWNERS, so a change there won't request review
 * here — check this copy if the semantics there move. Only the authorization
 * predicate and its helpers are vendored; the rest of that module has no Fay
 * consumer.
 */
import { ApolloError } from "@apollo/client";

/**
 * Semantic GraphQL error codes returned by backend services in
 * `extensions.error_code`. Values align with Apollo conventions.
 */
enum GraphQLErrorCode {
  Unauthenticated = "UNAUTHENTICATED",
  Unauthorized = "UNAUTHORIZED",
  Forbidden = "FORBIDDEN",
}

const AUTHORIZATION_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  GraphQLErrorCode.Forbidden,
  GraphQLErrorCode.Unauthorized,
  GraphQLErrorCode.Unauthenticated,
]);

// https://kentcdodds.com/blog/get-a-catch-block-error-message-with-typescript
type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as Record<string, unknown>).message === "string"
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) {
    return maybeError;
  }

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback for stringify maybeError (with circular references for example)
    return new Error(String(maybeError));
  }
}

/**
 * Parse message from an error object which may or may not be an instanceof Error
 * @param error
 * @returns error message
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof ApolloError) {
    return error.graphQLErrors.length > 0 ? error.graphQLErrors[0].message : error.message;
  }

  return toErrorWithMessage(error).message;
}

/**
 * Extract the semantic error code from the first GraphQL error's extensions.
 * Returns null if the error is not an ApolloError or no code is present.
 */
function getGraphQLErrorCode(error: unknown): string | null {
  if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
    const code = error.graphQLErrors[0].extensions?.error_code;
    return typeof code === "string" ? code : null;
  }
  return null;
}

/**
 * Check if an error is an authorization error.
 *
 * Prefers the semantic `extensions.error_code` (FORBIDDEN / UNAUTHORIZED /
 * UNAUTHENTICATED) set by backends that emit typed error codes, and falls back
 * to message-based matching for backends that have not been migrated yet.
 */
export function isAuthorizationError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const code = getGraphQLErrorCode(error);
  if (code !== null && AUTHORIZATION_ERROR_CODES.has(code)) {
    return true;
  }

  if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
    return error.graphQLErrors.some((gqlError) => {
      const message = gqlError.message?.toLowerCase() || "";
      return message.includes("not authorized") || message.includes("unauthorized");
    });
  }

  const errorMessage = getErrorMessage(error).toLowerCase();
  return errorMessage.includes("not authorized") || errorMessage.includes("unauthorized");
}
